import { useEffect, useMemo, useState } from "react";
import { ChevronDown, ChevronLeft, ChevronRight, X } from "lucide-react";
import { Link } from "react-router-dom";

import { getHomeNavbar } from "../../../services/homeNavbarApi";
import { buildCategoryUrl } from "../../../utils/buildCategoryUrl";
import "./HomeNavbar.css";

export default function HomeNavbar({ mobileMenuOpen, onCloseMobileMenu }) {
  const DEBUG_MEGA_MENU = true;
  const DEBUG_MENU_ID = 307; // Change to any menu id you want

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const [activeMenu, setActiveMenu] = useState(null);

  // Mobile navigation path
  const [mobilePath, setMobilePath] = useState([]);

  useEffect(() => {
    let mounted = true;

    async function loadNavbar() {
      try {
        setLoading(true);

        const data = await getHomeNavbar();

        if (mounted) {
          setItems(data);
        }
      } catch (error) {
        console.error("Failed to load navbar:", error);

        if (mounted) {
          setItems([]);
        }
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    }

    loadNavbar();

    return () => {
      mounted = false;
    };
  }, []);

  /*
    |--------------------------------------------------------------------------
    | Reset mobile navigation
    |--------------------------------------------------------------------------
    */

  useEffect(() => {
    if (!mobileMenuOpen) {
      setMobilePath([]);
    }
  }, [mobileMenuOpen]);

  /*
    |--------------------------------------------------------------------------
    | Current mobile level
    |--------------------------------------------------------------------------
    */

  const currentMobileItems = useMemo(() => {
    if (mobilePath.length === 0) {
      return items;
    }

    return mobilePath[mobilePath.length - 1]?.children || [];
  }, [items, mobilePath]);

  /*
    |--------------------------------------------------------------------------
    | Current mobile title
    |--------------------------------------------------------------------------
    */

  const currentMobileTitle = useMemo(() => {
    if (mobilePath.length === 0) {
      return "دسته‌بندی‌ها";
    }

    return mobilePath[mobilePath.length - 1]?.title || "دسته‌بندی‌ها";
  }, [mobilePath]);

  /*
    |--------------------------------------------------------------------------
    | Open next level
    |--------------------------------------------------------------------------
    */

  const openMobileLevel = (item) => {
    if (!item.children?.length) {
      return;
    }

    setMobilePath((prev) => [...prev, item]);
  };

  /*
    |--------------------------------------------------------------------------
    | Go back
    |--------------------------------------------------------------------------
    */

  const goBackMobile = () => {
    setMobilePath((prev) => prev.slice(0, -1));
  };

  /*
    |--------------------------------------------------------------------------
    | Close mobile menu
    |--------------------------------------------------------------------------
    */

  const closeMobileMenu = () => {
    setMobilePath([]);

    if (onCloseMobileMenu) {
      onCloseMobileMenu();
    }
  };

  if (loading) {
    return (
      <nav className="home-navbar" dir="rtl">
        <div className="home-navbar__container">
          <div className="home-navbar__loading">در حال بارگذاری منو...</div>
        </div>
      </nav>
    );
  }

  const activeMenuItem = items.find((item) => item.id === activeMenu);

  // const activeMenuItem = DEBUG_MEGA_MENU
  //     ? items.find(item => item.id === DEBUG_MENU_ID)
  //     : items.find(item => item.id === activeMenu);

  return (
    <>
      <nav
        className="home-navbar"
        dir="rtl"
        onMouseLeave={() => setActiveMenu(null)}
      >
        <div className="home-navbar__container">
          {/* Desktop menu */}

          <div className="home-navbar__desktop">
            {items.map((item) => (
              <div
                key={item.id}
                className="home-navbar__item"
                onMouseEnter={() => {
                  if (item.children?.length > 0) {
                    setActiveMenu(item.id);
                  } else {
                    setActiveMenu(null);
                  }
                }}
              >
                {item.url ? (
                  <Link
                    to={buildCategoryUrl(item, 1)}
                    // title={`ID: ${item.id}`}
                    className="home-navbar__link"
                  >
                    <span>{item.title}</span>

                    {item.children?.length > 0 && <ChevronDown size={15} />}
                  </Link>
                ) : (
                  <button
                    type="button"
                    className="home-navbar__link home-navbar__link--button"
                  >
                    <span>{item.title}</span>

                    {item.children?.length > 0 && <ChevronDown size={15} />}
                  </button>
                )}

                {/* {item.children?.length > 0 && (
                                    <MegaMenu item={item} />
                                )} */}
              </div>
            ))}
          </div>

          <>
            {activeMenuItem?.children?.length > 0 && (
              <MegaMenu item={activeMenuItem} />
            )}
          </>
        </div>
      </nav>

      {/* Mobile drawer */}
      {mobileMenuOpen && (
        <>
          <div className="home-navbar__overlay" onClick={closeMobileMenu} />

          <aside className="home-navbar__drawer" dir="rtl">
            {/* Drawer Header */}
            <div className="home-navbar__drawer-header">
              <div className="home-navbar__drawer-title">
                <img
                  src="images/logo.png"
                  className="home-navbar-img"
                  alt="cafedaru"
                />
              </div>

              <button
                type="button"
                className="home-navbar__close"
                onClick={closeMobileMenu}
                aria-label="بستن منو"
              >
                <X size={22} stroke="#ffff" />
              </button>
            </div>
            <div className="home-navbar__drawer-header-category">
              <div className="home-navbar__drawer-title">
                {mobilePath.length > 0 && (
                  <button
                    type="button"
                    className="home-navbar__back"
                    onClick={goBackMobile}
                    aria-label="بازگشت"
                  >
                    <ChevronRight size={22} />
                  </button>
                )}

                <strong>{currentMobileTitle}</strong>
              </div>
            </div>

            {/* Current level */}
            <div className="home-navbar__mobile-list">
              {currentMobileItems.length === 0 ? (
                <div className="home-navbar__empty">موردی وجود ندارد.</div>
              ) : (
                currentMobileItems.map((item) => (
                  <MobileMenuItem
                    key={item.id}
                    item={item}
                    level={mobilePath.length + 1}
                    onOpen={openMobileLevel}
                    onClose={closeMobileMenu}
                  />
                ))
              )}
            </div>
          </aside>
        </>
      )}
    </>
  );
}

function MegaGroup({ group, buildCategoryUrl }) {
  return (
    <>
      <Link to={buildCategoryUrl(group, 2)} className="home-navbar__mega-title">
        {group.title}
        <ChevronLeft size={18} />
      </Link>

      {!!group.children?.length && (
        // <ul className="home-navbar__mega-list">
        <>
          {group.children.map((child) => (
            <li key={child.id}>
              <Link
                to={buildCategoryUrl(child, 3)}
                className="home-navbar__mega-link"
              >
                {child.title}
              </Link>
            </li>
          ))}

          {/* </ul> */}
        </>
      )}
    </>
  );
}

function MegaMenu({ item }) {
  if (!item?.children?.length) return null;

  return (
    <div className="home-navbar__mega">
      <div className="home-navbar__mega-container">
        {item.children.map((group) => (
          <MegaGroup
            key={group.id}
            group={group}
            buildCategoryUrl={buildCategoryUrl}
          />
        ))}
      </div>
    </div>
  );
}

function MobileMenuItem({ item, level, onOpen, onClose }) {
  const hasChildren = item.children?.length > 0;

  const categoryUrl = item.url ? buildCategoryUrl(item, level) : null;

  return (
    <div className="home-navbar__mobile-item">
      <div className="home-navbar__mobile-row">
        {/* Item link */}
        {categoryUrl ? (
          <Link
            to={categoryUrl}
            className="home-navbar__mobile-link"
            onClick={onClose}
          >
            {item.title}
          </Link>
        ) : (
          <span className="home-navbar__mobile-link">{item.title}</span>
        )}

        {/* Open children */}
        {hasChildren && (
          <button
            type="button"
            className="home-navbar__mobile-next"
            onClick={() => onOpen(item)}
            aria-label={`نمایش ${item.title}`}
          >
            <ChevronLeft size={21} />
          </button>
        )}
      </div>
    </div>
  );
}
